/**
 * 94. Country Pack Manager — installed packs; versions; enable/disable;
 * update; custom pack request. Country packs localize tax, calendar,
 * number/date/address formats, statutory reports, and e-invoicing.
 *
 * Arabic and English (C7): every word from the catalogs. A country pack
 * (`code` country-<ISO>) is named by its country in the interface's language
 * («حزمة مصر»), not by the English name the pack definition carries; any
 * other pack keeps its name.
 *
 * FIX-CERT: every row shows its certification state as a label, and one click
 * opens what the label means (Decision 230): the status, the source the
 * pack's own content states, the date, "not reviewed by a licensed
 * accountant" and management's responsibility. What the data does not record
 * (a reviewer, a date) is shown as not recorded, never guessed. The state is
 * a label only: no pack is hidden or disabled for it.
 */
import { useEffect, useId, useRef, useState } from "react";
import {
  Card,
  PageHeader,
  Badge,
  Button,
  DataTable,
  Skeleton,
} from "@/components/ui";
import { PendingButton, PendingNote } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation, type TFunction } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { countryName } from "@/lib/reference";

/** One part of the pack's content that states its own basis (the API's). */
interface PackPartSource {
  part: string;
  status: string | null;
  basis: string | null;
  references: string[];
}

/** GET /v1/localization/country-packs `certification` (FIX-CERT). */
interface PackCertification {
  state: "Default" | "Certified";
  reviewer: string | null;
  reviewedAt: string | null;
  sources: PackPartSource[];
}

interface CountryPack {
  id: string;
  code: string;
  name: string;
  version: number;
  installedVersion: number | null;
  status: "Installed" | "Upgraded" | "RolledBack" | "Available";
  localizations: string[];
  updateAvailable: boolean;
  /** Absent only from an API older than FIX-CERT: shown as not stated. */
  certification?: PackCertification;
}

/** "Egypt Country Pack" by its code, in the interface's language. */
function packName(t: TFunction, language: string, pack: CountryPack): string {
  const iso = /^country-([a-z]{2})$/.exec(pack.code)?.[1]?.toUpperCase();
  return iso
    ? t("admin.countryPacks.packName", {
        country: countryName(iso, language),
      })
    : pack.name;
}

/** The label's words for a state, in the interface's language. */
function stateLabel(t: TFunction, pack: CountryPack): string {
  const state = pack.certification?.state;
  return state
    ? t(`admin.countryPacks.reviewState.${state}`, { defaultValue: state })
    : t("admin.countryPacks.reviewUnknown");
}

/** A part's own word (`uncertified`, `standard-based`), else as stored. */
function word(t: TFunction, value: string): string {
  return t(`admin.countryPacks.explain.words.${value}`, {
    defaultValue: value,
  });
}

/**
 * What the label means for one pack. Pack content is stored in English, so
 * a basis sentence and every reference keep their own language and
 * direction inside an Arabic screen.
 */
function ReviewExplanation({
  pack,
  name,
  onClose,
}: {
  pack: CountryPack;
  name: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLElement>(null);
  const titleId = useId();
  // Opening it moves focus here, so a keyboard or screen-reader user lands
  // on the explanation rather than staying on a row far below it.
  useEffect(() => {
    ref.current?.focus();
  }, [pack.id]);
  const c = pack.certification;
  const state = c?.state;
  const termStyle = { fontWeight: 600, marginBlockStart: 8 } as const;
  return (
    <section
      ref={ref}
      tabIndex={-1}
      aria-labelledby={titleId}
      className="atlas-card"
      style={{ marginBlockEnd: 12 }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <h3 id={titleId} className="atlas-h2" style={{ margin: 0, flex: 1 }}>
          {t("admin.countryPacks.explain.title", { pack: name })}
        </h3>
        <Button size="sm" onClick={onClose}>
          {t("admin.countryPacks.explain.close")}
        </Button>
      </div>
      <dl style={{ margin: 0 }}>
        <dt style={termStyle}>{t("admin.countryPacks.explain.status")}</dt>
        <dd style={{ marginInlineStart: 0 }}>
          {state
            ? t(`admin.countryPacks.explain.${state}`, { defaultValue: state })
            : t("admin.countryPacks.reviewUnknown")}
        </dd>
        <dt style={termStyle}>{t("admin.countryPacks.explain.reviewer")}</dt>
        <dd style={{ marginInlineStart: 0 }}>
          {c?.reviewer ?? t("admin.countryPacks.explain.notReviewed")}
        </dd>
        <dt style={termStyle}>{t("admin.countryPacks.explain.date")}</dt>
        <dd style={{ marginInlineStart: 0 }}>
          {c?.reviewedAt ?? t("admin.countryPacks.explain.notRecorded")}
        </dd>
        <dt style={termStyle}>{t("admin.countryPacks.explain.sources")}</dt>
        <dd style={{ marginInlineStart: 0 }}>
          {!c || c.sources.length === 0 ? (
            t("admin.countryPacks.explain.noSources")
          ) : (
            <ul style={{ margin: 0, paddingInlineStart: 20 }}>
              {c.sources.map((s) => (
                <li key={s.part} style={{ marginBlockEnd: 4 }}>
                  <strong>
                    {t(
                      `admin.countryPacks.explain.parts.${s.part.replace(/\./g, "_")}`,
                      { defaultValue: s.part },
                    )}
                  </strong>
                  {s.status && <> · {word(t, s.status)}</>}
                  {s.basis && (
                    <>
                      {" · "}
                      <bdi lang="en">{word(t, s.basis)}</bdi>
                    </>
                  )}
                  {s.references.length > 0 && (
                    <ul style={{ margin: 0, paddingInlineStart: 20 }}>
                      {s.references.map((r) => (
                        <li key={r}>
                          <bdi lang="en">{r}</bdi>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </dd>
        <dt style={termStyle}>
          {t("admin.countryPacks.explain.responsibility")}
        </dt>
        <dd style={{ marginInlineStart: 0 }}>
          {t("admin.countryPacks.explain.responsibilityText")}
        </dd>
      </dl>
    </section>
  );
}

export function CountryPackManagerPage() {
  const { t, i18n } = useTranslation();
  const installId = useId();
  const upgradeId = useId();
  const { data, isLoading } = useApiList<CountryPack>(
    ["localization", "country-packs"],
    "/localization/country-packs",
  );
  const [openId, setOpenId] = useState<string | null>(null);
  const version = (n: number) => t("shared.versionNumber", { version: n });
  const open = data?.items.find((r) => r.id === openId) ?? null;

  return (
    <div>
      <PageHeader
        title={t("admin.countryPacks.title")}
        subtitle={t("admin.countryPacks.subtitle")}
      />
      {/* Install, upgrade and disable wait for the pack installer: one
          sentence each, shared by every row (UI-HONEST). The custom-pack
          request button was removed: no plan takes such requests. */}
      <PendingNote id={installId} reason="packInstall" slice="CF-2" />
      {data?.items.some(
        (r) => r.installedVersion !== null && r.updateAvailable,
      ) && <PendingNote id={upgradeId} reason="packUpgrade" slice="CF-4" />}
      {isLoading || !data ? (
        <Skeleton rows={8} />
      ) : (
        <Card title={listTitle(t("admin.countryPacks.list"), data.total)}>
          {open && (
            <ReviewExplanation
              pack={open}
              name={packName(t, i18n.language, open)}
              onClose={() => setOpenId(null)}
            />
          )}
          <DataTable<CountryPack>
            rows={data.items}
            columns={[
              {
                key: "name",
                label: t("admin.countryPacks.pack"),
                render: (r) => packName(t, i18n.language, r),
              },
              {
                key: "code",
                label: t("fields.code"),
                render: (r) => <span className="atlas-mono">{r.code}</span>,
              },
              {
                key: "installedVersion",
                label: t("admin.countryPacks.installed"),
                numeric: true,
                render: (r) =>
                  r.installedVersion === null
                    ? "—"
                    : version(r.installedVersion),
              },
              {
                key: "version",
                label: t("admin.countryPacks.latest"),
                numeric: true,
                render: (r) => version(r.version),
              },
              {
                key: "certification",
                label: t("admin.countryPacks.review"),
                // One click opens what the label means; the label itself is
                // the button's text, so it is read as it is shown.
                render: (r) => (
                  <button
                    type="button"
                    aria-expanded={openId === r.id}
                    onClick={() =>
                      setOpenId((cur) => (cur === r.id ? null : r.id))
                    }
                    style={{
                      background: "none",
                      border: 0,
                      padding: 0,
                      cursor: "pointer",
                      font: "inherit",
                    }}
                  >
                    <Badge
                      tone={
                        r.certification?.state === "Certified"
                          ? "success"
                          : "warning"
                      }
                    >
                      {stateLabel(t, r)}
                    </Badge>
                  </button>
                ),
              },
              {
                key: "updateAvailable",
                label: t("admin.countryPacks.update"),
                render: (r) =>
                  r.updateAvailable ? (
                    <Badge tone="warning">
                      {t("admin.countryPacks.available")}
                    </Badge>
                  ) : (
                    <Badge tone="success">
                      {t("admin.countryPacks.current")}
                    </Badge>
                  ),
              },
              {
                key: "id",
                label: t("fields.actions"),
                render: (r) =>
                  r.installedVersion === null ? (
                    <PendingButton
                      size="sm"
                      variant="primary"
                      reasonId={installId}
                    >
                      {t("admin.countryPacks.install")}
                    </PendingButton>
                  ) : r.updateAvailable ? (
                    <PendingButton size="sm" reasonId={upgradeId}>
                      {t("admin.countryPacks.upgrade")}
                    </PendingButton>
                  ) : (
                    <PendingButton size="sm" reasonId={installId}>
                      {t("admin.countryPacks.disable")}
                    </PendingButton>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
